import { useEffect } from "react"
import { useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "urql"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Loader2 } from "lucide-react"
import { FieldError } from "@/components/shared/field-error"
import { streamConsumerSchema, type StreamConsumerFormValues } from "@/lib/schemas"
import { CreateStreamConsumerMutation } from "@/lib/graphql/streaming"

interface ConsumerFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  streamId: string
}

export function ConsumerForm({ open, onOpenChange, onSuccess, streamId }: ConsumerFormProps) {
  const [, create] = useMutation(CreateStreamConsumerMutation)

  const form = useForm<StreamConsumerFormValues>({
    resolver: zodResolver(streamConsumerSchema) as Resolver<StreamConsumerFormValues>,
    defaultValues: {
      streamId,
      name: "",
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({ streamId, name: "" })
    }
  }, [open, streamId, form])

  const onSubmit = async (values: StreamConsumerFormValues) => {
    const result = await create({ input: { streamId: values.streamId, name: values.name } })
    if (result.error) { toast.error(result.error.message); return }
    toast.success("Consumer created")
    onSuccess()
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Consumer</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label htmlFor="consumerName">Name</Label>
              <Input id="consumerName" {...form.register("name")} placeholder="my-consumer" />
              <FieldError error={form.formState.errors.name} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
